import { Skeleton } from '@mantine/core';
import { notifications } from '@mantine/notifications';
import { IconPlus } from '@tabler/icons-react';
import { AxiosError } from 'axios';
import { RevisionTemplateGetResponse as RevisionTemplate } from 'api/actions/revision-template-get/revision-template-get-response';
import { useApi } from 'api/api-context';
import RevisionTemplateForm, {
  RevisionTemplateFormData,
} from 'components/forms/revision-template/RevisionTemplateForm';
import { FEATURE_TOGGLE_REVISION_TEMPLATE_EDITOR } from 'env';
import panic from 'errors/panic';
import DashboardLayout from 'layouts/dashboard/DashboardLayout';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Navigate, useNavigate, useParams } from 'react-router-dom';
import { DASHBOARD_PAGE_PATH, REVISION_TEMPLATES_PAGE_PATH, TEMPLATE_EDITOR_PAGE_PATH } from 'routes/paths';

/**
 * Allows the user to copy an existing revision template.
 */
function CopyRevisionTemplatePageImpl({ revisionTemplateId }: { revisionTemplateId: number }) {
  const navigate = useNavigate();
  const createdTemplateId = useRef<number>();
  const { getAction } = useApi();
  const [template, setTemplate] = useState<RevisionTemplate | null>(null);
  const [notFound, setNotFound] = useState(false);

  const initialValues: RevisionTemplateFormData | undefined = useMemo(() => {
    if (!template) {
      return undefined;
    }

    return {
      deviceTypeId: String(template.deviceTypeId),
      name: `${template.name} (Kópia)`,
      kinds: template.kinds,
      organizations: template.organizations ?? [],
      blacklistedOrganizations: template.isGlobal ? template.blacklistedOrganizations : [],
      restriction: !template.isGlobal && template.isOrganization ? 'organization' : 'global',
      slug: '', // Will be auto-generated
      supportsKep: template.supportsKep,
      singleDeviceOnly: template.singleDeviceOnly,
    } satisfies RevisionTemplateFormData;
  }, [template]);

  const onSubmit = useCallback(
    async ({
      name,
      slug,
      kinds,
      deviceTypeId,
      restriction,
      organizations,
      blacklistedOrganizations = [],
      supportsKep,
      singleDeviceOnly,
    }: RevisionTemplateFormData) => {
      const isOrganization = restriction === 'organization';

      const revisionTemplateCreateAction = getAction('RevisionTemplateCreate');

      const { revisionTemplateId } = await revisionTemplateCreateAction({
        payload: {
          name,
          slug,
          kinds,
          deviceTypeId: Number(deviceTypeId),
          isOrganization,
          isGlobal: restriction === 'global',
          organizations: isOrganization ? organizations.map(({ organizationId }) => organizationId) : [],
          blacklistedOrganizations: isOrganization
            ? []
            : blacklistedOrganizations.map(({ organizationId }) => organizationId),
          supportsKep,
          singleDeviceOnly,
          formScheme: template?.formScheme,
          templateScheme: template?.templateScheme,
          visualData: template?.visualData,
          pipelineScheme: template?.pipelineScheme,
        },
      });
      notifications.show({
        title: 'Šablóna revíznej správy bola úspešne vytvorená',
        message: `Šablóna revíznej správy ${name} bola úspešne vytvorená.`,
      });

      createdTemplateId.current = revisionTemplateId;
    },
    [template]
  );

  useEffect(() => {
    const revisionGetRevisionTemplateAction = getAction('RevisionTemplateGet');

    revisionGetRevisionTemplateAction({ parameters: { revisionTemplateId: String(revisionTemplateId) } })
      .then(setTemplate)
      .catch((error) => {
        if (error instanceof AxiosError && error.response?.status === 404) {
          setNotFound(true);
          return;
        }

        panic(error as Error);
      });
  }, [revisionTemplateId]);

  if (notFound) {
    return <Navigate to={REVISION_TEMPLATES_PAGE_PATH.original} replace />;
  }

  /** Opens the saved template after the form has cleared its dirty state. */
  function onSaved() {
    if (FEATURE_TOGGLE_REVISION_TEMPLATE_EDITOR) {
      navigate(TEMPLATE_EDITOR_PAGE_PATH.insert({ revisionTemplateId: createdTemplateId.current }));
    } else {
      navigate(REVISION_TEMPLATES_PAGE_PATH.original);
    }
  }

  return (
    <DashboardLayout
      title="Pridanie novej šablóny revíznej správy"
      breadcrumbs={[
        { title: 'Šablóny revíznych správ', link: REVISION_TEMPLATES_PAGE_PATH.original },
        { title: 'Pridanie novej šablóny revíznej správy' },
      ]}
    >
      {template === null ? (
        <Skeleton height={500} />
      ) : (
        <RevisionTemplateForm
          onSubmit={onSubmit}
          onSaved={onSaved}
          initialValues={initialValues}
          primaryButtonText="Vytvoriť"
          primaryIcon={<IconPlus stroke="1.5" height={24} width={24} />}
        />
      )}
    </DashboardLayout>
  );
}

/**
 * Allows the user to copy an existing revision template.
 */
export default function CopyRevisionTemplatePage() {
  const { revisionTemplateId: revisionTemplateIdRaw } = useParams();
  const revisionTemplateId = useMemo(() => Number(revisionTemplateIdRaw), [revisionTemplateIdRaw]);

  if (isNaN(revisionTemplateId)) {
    return <Navigate to={DASHBOARD_PAGE_PATH.original} replace />;
  }

  return <CopyRevisionTemplatePageImpl revisionTemplateId={revisionTemplateId} />;
}
